import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { BiPhone, BiSearch, BiX } from "react-icons/bi"
import { FaEye } from "react-icons/fa6"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  fetchAllRestaurantIssues,
  updateRestaurantIssue,
} from "../../../redux/thunk"
import { setSelectedIssue } from "../../../redux/restaurantIssues/restaurantIssuesSlice"
import { RestaurantIssue } from "../../../types/restaurantIssues"
import toast from "react-hot-toast"
import LoadingSpinner from "../../../components/elements/LoadingSpinner"

// Custom Button Component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  onClick,
  ...props
}: {
  children: React.ReactNode
  variant?: "default" | "ghost" | "destructive" | "secondary"
  size?: "default" | "sm"
  className?: string
  onClick?: () => void
  [key: string]: any
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    destructive: "bg-red-500 text-white hover:bg-red-600",
    secondary: "bg-gray-100 text-gray-900 hover:bg-gray-200",
  }

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 text-sm",
  }

  return (
    <button className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  )
}

// Custom Input Component
const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

// Custom Card Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}>{children}</div>
}

const CardHeader = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
}

const CardTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
}

const CardContent = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>
}

// Custom Badge Component
const Badge = ({
  children,
  variant = "default",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "default" | "secondary" | "destructive";
  className?: string;
}) => {
  const variants = {
    default: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-secondary-foreground",
    destructive: "bg-[#EA1919] text-white",
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${variants[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

export default function RestaurantIssues() {
  const dispatch = useAppDispatch()
  const { issues, loading, selectedIssue } = useAppSelector((state) => state.restaurantIssues)
  
  const [showIssueModal, setShowIssueModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("All Statuses")
  const [responseMessage, setResponseMessage] = useState("")
  const [newStatus, setNewStatus] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    dispatch(fetchAllRestaurantIssues())
  }, [dispatch])

  // Calculate metrics from issues
  const metrics = useMemo(() => {
    const open = issues.filter((issue) => issue.status === "Open" || issue.status === "Pending").length
    const inProgress = issues.filter((issue) => issue.status === "In Progress").length
    const resolvedToday = issues.filter((issue) => {
      if (issue.resolvedAt) {
        const resolvedDate = new Date(issue.resolvedAt).toDateString()
        const today = new Date().toDateString()
        return resolvedDate === today
      }
      return false
    }).length
    const highPriority = issues.filter((issue) => issue.priority === "High" || issue.priority === "Critical").length
    
    return { open, inProgress, resolvedToday, highPriority }
  }, [issues])

  // Filter issues based on search and status
  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      const matchesSearch =
        searchTerm === "" ||
        issue.restaurantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.title.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus =
        statusFilter === "All Statuses" || issue.status === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [issues, searchTerm, statusFilter])

  const handleIssueDetails = (issue: RestaurantIssue) => {
    dispatch(setSelectedIssue(issue))
    setNewStatus(issue.status)
    setResponseMessage(issue.adminResponse || "")
    setShowIssueModal(true)
  }

  const handleUpdateIssue = async () => {
    if (!selectedIssue || !responseMessage.trim()) {
      toast.error("Please provide a response message")
      return
    }

    setIsSubmitting(true)
    try {
      const result: any = await dispatch(
        updateRestaurantIssue({
          issueId: selectedIssue._id,
          responseMessage,
          status: newStatus || selectedIssue.status,
        })
      )

      if (result.error) {
        throw new Error(result.payload || "Failed to update issue")
      }

      toast.success("Issue updated successfully")
      setShowIssueModal(false)
      setResponseMessage("")
      dispatch(fetchAllRestaurantIssues())
    } catch (error: any) {
      toast.error(error.message || "Failed to update issue")
    } finally {
      setIsSubmitting(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Open":
      case "Pending":
        return "bg-blue-100 text-blue-800"
      case "In Progress":
        return "bg-yellow-100 text-yellow-800"
      case "Resolved":
      case "Closed":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case "High":
      case "Critical":
        return "bg-red-100 text-red-800"
      case "Medium":
        return "bg-yellow-100 text-yellow-800"
      case "Low":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header className="mb-4 h-16 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center justify-between">
          <div>
            <div className="relative flex items-center">
              <span className="text-lg font-semibold">
                Restaurants
              </span>
              <span className="mx-2 text-gray-400">/</span>
              <span className="text-lg font-semibold text-green-600">
                Issues
              </span>
            </div>
            <p className="-mt-2 text-sm text-gray-500">
              Track and resolve restaurant partner issues, complaints, and technical problems.
            </p>
          </div>
        </div>
      </header>

      {/* Issues Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="flex flex-col border-l-4 border-l-green-600">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Open Issues</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">{metrics.open}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col border-l-4 border-l-yellow-500">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">In Progress</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">{metrics.inProgress}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col border-l-4 border-l-green-600">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Resolved Today</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">{metrics.resolvedToday}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-start space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">High Priority</CardTitle>
          </CardHeader>
          <CardContent className="mt-auto">
            <div className="text-2xl font-bold">{metrics.highPriority}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-x-6 justify-between mb-6">
        <div className="relative w-full">
          <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input 
            placeholder="Search restaurant by name or issue ID" 
            className="pl-10 h-10 w-[700px]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <select 
            className="h-10 rounded-md border border-input px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option>All Statuses</option>
            <option>Open</option>
            <option>Pending</option>
            <option>In Progress</option>
            <option>Resolved</option>
            <option>Closed</option>
          </select>
        </div>
      </div>

      {/* Issues List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      ) : filteredIssues.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-gray-500">No issues found</p>
          </CardContent>
        </Card>
      ) : (
      <div className="space-y-4">
        {filteredIssues.map((issue) => (
        <Card key={issue._id}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{issue._id.slice(-8).toUpperCase()}</span>
                  <span className="text-green-600">•</span>
                  <span className="text-sm text-gray-600">{issue.title}</span>
                </div>
                <p className="text-sm text-gray-500">{issue.restaurantName} • {issue.priority} Priority</p>
                <p className="text-xs text-green-600">{formatDate(issue.createdAt)}</p>
              </div>
              <div className="flex items-center gap-x-10">
                <Badge className={getPriorityBadgeClass(issue.priority)}>{issue.priority} Priority</Badge>
                <Badge className={getStatusBadgeClass(issue.status)}>{issue.status}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-green-600 rounded-lg border border-[#000000BF] text-[#0000008C] space-x-2"
                  onClick={() => handleIssueDetails(issue)}
                >
                  <span>View</span>
                  <FaEye className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        ))}

      </div>
      )}

      {/* Issue Details Modal */}
      {showIssueModal && selectedIssue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-green-600">Issue Details</h3>
                <p className="text-sm text-gray-500">
                  View detailed information about issue {selectedIssue._id.slice(-8).toUpperCase()}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowIssueModal(false)}>
                <BiX className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">{selectedIssue.title}</h4>
                  <div className="flex gap-2 mt-2">
                    <Badge className={getPriorityBadgeClass(selectedIssue.priority)}>{selectedIssue.priority}</Badge>
                    <Badge className={getStatusBadgeClass(selectedIssue.status)}>{selectedIssue.status}</Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Restaurant</h5>
                  <p className="text-sm text-gray-700">{selectedIssue.restaurantName}</p>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Restaurant ID</h5>
                  <p className="text-sm text-gray-700">{selectedIssue.restaurantId}</p>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-2">Description</h5>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{selectedIssue.description}</p>
                </div>
              </div>

              {selectedIssue.adminResponse && (
                <div>
                  <h5 className="font-medium mb-2">Admin Response</h5>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-sm text-gray-700">{selectedIssue.adminResponse}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium mb-2">Created At</h5>
                  <p className="text-sm text-gray-700">{formatDate(selectedIssue.createdAt)}</p>
                </div>
                {selectedIssue.resolvedAt && (
                  <div>
                    <h5 className="font-medium mb-2">Resolved At</h5>
                    <p className="text-sm text-gray-700">{formatDate(selectedIssue.resolvedAt)}</p>
                  </div>
                )}
              </div>

              <div>
                <h5 className="font-medium mb-2">Response Message</h5>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={4}
                  placeholder="Enter your response message..."
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                />
              </div>

              <div>
                <h5 className="font-medium mb-2">Update Status</h5>
                <select
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <option value="Open">Open</option>
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>

              <div className="flex gap-3">
                <Button 
                  className="bg-green-600 text-white hover:bg-green-700"
                  onClick={handleUpdateIssue}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Issue"}
                </Button>
                <Button 
                  variant="ghost" 
                  className="border border-gray-300"
                  onClick={() => setShowIssueModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}